/**
 * The site-wide footer.
 *
 * ── WHERE ITS CONTENT COMES FROM ────────────────────────────────────────────
 *   company block, social links, payment brands  ->  GET /api/site-info
 *   legal document links                         ->  GET /api/consent/documents
 *   language                                     ->  engine/i18n runtime
 *   currency                                     ->  GET|PATCH /me/preferences
 *
 * `/site-info` was built to feed a footer — it returns the company block, the
 * enabled social links and the enabled payment methods and, until now, the app
 * read one boolean out of it. This is the consumer it was waiting for.
 *
 * ── EVERY COMPANY FIELD AN OPERATOR CAN EDIT, AND WHERE IT LANDS ────────────
 *   name          -> the brand line, the copyright line, the hero lockup
 *   game_name     -> the same three, as the fallback when `name` is blank
 *   email         -> "Email us at …" and the Support column
 *   address       -> the postal block
 *   country       -> the last line of that postal block
 *   jurisdiction  -> the registration line, alone or with the number
 *   registration  -> the registration line, alone or with the jurisdiction
 *   domain        -> DELIBERATELY NOT RENDERED. See `NOT-RENDERED` below.
 *
 * ── NOT-RENDERED: company.domain ───────────────────────────────────────────
 * It is the one field whose value the visitor is already looking at — it is in
 * the address bar. Printed here it would be worth nothing as a trust signal
 * either, because a cloned site copies page text and cannot copy the address
 * bar, so "the official site is riftgate.example" reassures a visitor of a
 * phishing page exactly as loudly as it reassures a visitor of the real one.
 * Every other field above tells someone something the page cannot; this one
 * does not, so it stays what it is — configuration (the canonical host), not a
 * statement about who the company is. If it is ever wanted on screen it is one
 * line next to `postalBlock`, and this note is the reason it is not there.
 *
 * ── EVERY BLOCK IS CONDITIONAL, AND THAT IS THE DESIGN ──────────────────────
 * In a preview build (`PREVIEW_BUILD=true`) the server deliberately blanks the
 * company, the socials and the payment list. A footer that rendered its
 * headings regardless would show four empty columns and "© 2026 ." — a page
 * that looks broken rather than one that is deliberately anonymous. So a block
 * renders only when it has real content, and preview mode gets an explicit
 * notice saying what has been switched off. Nothing here invents a placeholder
 * to fill a hole.
 *
 * ── RE-RENDER AND FOCUS ─────────────────────────────────────────────────────
 * Changing the language rebuilds the whole footer, including the `<select>`
 * that was just used. Without `restoreFocus` a keyboard or screen-reader user
 * would be dropped onto `<body>` — silently sent to the top of the document —
 * every time they changed language. The stable control ids in `switchers.ts`
 * exist for this.
 */
import { formatNumber, getLocaleTag, subscribeI18n, t, tParts } from '../i18n';
import { brandHeroLockup } from './brand';
import { COOKIE_SETTINGS_HASH } from './cookie-consent';
import { TAP_TARGET, VISUALLY_HIDDEN, clear, el, type Child } from './dom';
import { skinButton } from '../ui/button';
import { attachPress } from '../ui/press';
import { subscribeCurrency } from './currency';
import {
  loadLegalDocuments,
  legalDocumentPath,
  peekLegalDocuments,
  type LegalDocumentLink,
} from './legal-links';
import { ROUTES } from './routes';
import {
  EMPTY_SITE_INFO,
  brandName,
  loadSiteInfo,
  paymentIconUrl,
  socialIconUrl,
  peekSiteInfo,
  type GameSiteInfo,
  type PaymentMethodRef,
  type SiteCompany,
} from './site-info';
import { createCurrencySwitcher, createLanguageSwitcher, type SwitcherHandle } from './switchers';

export interface FooterHandle {
  /** The `<footer>` element. Already appended to the host. */
  readonly element: HTMLFooterElement;
  /** Rebuild from current data. Called automatically on language/currency change. */
  refresh(): void;
  destroy(): void;
}

/** `HTMLFooterElement` is not a real DOM interface — `<footer>` is an HTMLElement. */
type HTMLFooterElement = HTMLElement;

const FOOTER_CLASS =
  'mt-auto border-t border-border bg-surface-2 text-[13px] text-text-dim ' +
  // Safe areas: on a notched phone in landscape the left/right insets are real,
  // and a footer is the one region that sits under the home indicator.
  'ps-[max(1rem,env(safe-area-inset-left))] pe-[max(1rem,env(safe-area-inset-right))] ' +
  'pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-10';
const INNER_CLASS = 'mx-auto flex w-full max-w-6xl flex-col gap-8';
const COLUMN_HEADING_CLASS = 'mb-2 font-heading text-sm uppercase tracking-wide text-text';
const LINK_CLASS = `${TAP_TARGET} text-text-dim hover:text-text hover:underline`;
/**
 * Social links carry the full-strength body colour, not the dimmed nav colour —
 * they read as part of the footer's own voice rather than as fine print, and
 * `bg-current` below tints the brand mark to match. Kept SEPARATE from
 * `LINK_CLASS` on purpose: that constant is shared with the legal and support
 * columns, which stay dimmed.
 */
const SOCIAL_LINK_CLASS = `${TAP_TARGET} text-text hover:underline`;

/**
 * Mount the footer into `host`.
 *
 * Renders synchronously with whatever is already cached, then again as
 * `/site-info` and the legal document list arrive. A footer must never be the
 * reason a page is blank while the network is slow.
 */
export function mountFooter(host: HTMLElement): FooterHandle {
  const footer = el('footer', { class: FOOTER_CLASS, 'data-testid': 'site-footer' });
  host.appendChild(footer);

  let destroyed = false;
  let info: GameSiteInfo = peekSiteInfo() ?? EMPTY_SITE_INFO;
  let legal: readonly LegalDocumentLink[] = peekLegalDocuments() ?? [];
  const switchers: SwitcherHandle[] = [];

  const render = (): void => {
    if (destroyed) return;
    const focusedId =
      document.activeElement instanceof HTMLElement &&
      footer.contains(document.activeElement) &&
      document.activeElement.id !== ''
        ? document.activeElement.id
        : null;

    for (const switcher of switchers.splice(0)) switcher.destroy();
    clear(footer);
    footer.appendChild(buildInner(info, legal, switchers));

    if (focusedId !== null) {
      // `getElementById` rather than a `#id` selector: an id is not necessarily
      // a valid CSS identifier, and `CSS.escape` is not available everywhere.
      const restored = document.getElementById(focusedId);
      if (restored !== null && footer.contains(restored)) restored.focus();
    }
  };

  // Both call back immediately, so the footer is fully rendered before
  // `mountFooter` returns rather than one frame later.
  const offI18n = subscribeI18n(() => render());
  const offCurrency = subscribeCurrency(() => render());

  void loadSiteInfo().then((loaded) => {
    if (destroyed) return;
    info = loaded;
    render();
  });
  void loadLegalDocuments().then((documents) => {
    if (destroyed) return;
    legal = documents;
    render();
  });

  return {
    element: footer,
    refresh: render,
    destroy(): void {
      destroyed = true;
      offI18n();
      offCurrency();
      for (const switcher of switchers.splice(0)) switcher.destroy();
      footer.remove();
    },
  };
}

/* ══════════════════════════════════════════════════════════ the tree ════ */

function buildInner(
  info: GameSiteInfo,
  legal: readonly LegalDocumentLink[],
  switchers: SwitcherHandle[],
): HTMLElement {
  const language = createLanguageSwitcher();
  const currency = createCurrencySwitcher();
  switchers.push(language, currency);

  return el('div', { class: INNER_CLASS }, [
    // The brand lockup opens the footer — the same painted wordmark the
    // marketing hero uses, kept small here so it reads as a sign-off.
    el('a', { href: ROUTES.home, class: `${TAP_TARGET} inline-flex`, 'aria-label': brandName(info) }, [
      brandHeroLockup({ name: brandName(info), class: 'h-14 w-auto sm:h-16' }),
    ]),
    el(
      'div',
      { class: 'grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4' },
      [companyBlock(info), legalNav(legal), supportNav(info), communityNav(info)],
    ),
    paymentStrip(info),
    el('div', { class: 'flex flex-col gap-6 border-t border-border pt-6' }, [
      el('div', { class: 'flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-8' }, [
        language.element,
        currency.element,
      ]),
      el('div', { class: 'flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between' }, [
        el('div', { class: 'flex flex-col gap-1' }, [
          el('p', { text: copyrightLine(info) }),
          registrationLine(info),
        ]),
        backToTop(),
      ]),
      previewNotice(info),
    ]),
  ]);
}

/** The company block: who is behind the site, and how to reach them. */
function companyBlock(info: GameSiteInfo): HTMLElement {
  const { company } = info;
  const details: Child[] = [postalBlock(company)];

  if (company.email !== '') details.push(contactLine(company.email));

  return el('section', { class: 'flex flex-col', 'aria-labelledby': 'footer-company' }, [
    el('h2', { id: 'footer-company', class: VISUALLY_HIDDEN, text: t('footer.company_label') }),
    el('p', { class: 'font-heading text-base text-text', text: brandName(info) }),
    el('p', { class: 'mt-1 max-w-[24rem]', text: t('footer.tagline') }),
    ...details,
  ]);
}

/**
 * The trading address, written the way an envelope is: the operator's own
 * lines first, the country last.
 *
 * WHY IT MATTERS. A game that sells to EU consumers has to make its trading
 * address findable, and the footer is where a visitor looks for it. Both halves
 * are operator-editable, both are on `/site-info`, and until now the country
 * reached no surface at all.
 *
 * BLANKS ARE NOT LINES. A fresh install has every field empty, so the block
 * renders only when something is in it, blank lines inside a pasted address are
 * dropped, and a country with no street prints as a country with no street —
 * never a stray comma, never a label over nothing.
 */
function postalBlock(company: SiteCompany): HTMLElement | null {
  const lines = addressLines(company.address);
  const country = countryLine(company.country);
  if (country !== '') lines.push(country);
  if (lines.length === 0) return null;

  return el('div', { class: 'mt-3' }, [
    el('p', { class: 'text-text-dim/80', text: t('footer.address_label') }),
    // The operator enters an address as free text with line breaks; an
    // <address> preserves them without us guessing at a format per country.
    el('address', {
      class: 'not-italic whitespace-pre-line',
      'data-testid': 'footer-address',
      text: lines.join('\n'),
    }),
  ]);
}

/** A pasted address, split into the lines worth printing. */
function addressLines(address: string): string[] {
  return address
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * The country, as a reader of THIS page's language would name it.
 *
 * The admin field is free text, so an operator types either "Estonia" or the
 * ISO code an international address actually carries ("EE"). A bare code is
 * expanded — the footer re-renders on every language change, so it follows the
 * visitor's language the way the rest of the footer does. Anything else is
 * printed exactly as typed: guessing at an operator's words is how a footer
 * starts inventing.
 */
function countryLine(country: string): string {
  const value = country.trim();
  if (!/^[A-Za-z]{2}$/.test(value)) return value;
  try {
    // Not every engine ships DisplayNames, and `of()` rejects a code it cannot
    // parse. Either way the typed value is still the honest answer.
    const names = new Intl.DisplayNames([getLocaleTag()], { type: 'region' });
    return names.of(value.toUpperCase()) ?? value;
  } catch {
    return value;
  }
}

/**
 * "Email us at {email}" as ONE translatable sentence with a link inside it.
 *
 * `tParts` is what makes that possible: concatenating a translated prefix with
 * a link would fix English word order into every language.
 */
function contactLine(email: string): HTMLElement {
  const parts = tParts('footer.contact_email', { email });
  const line = el('p', { class: 'mt-3' });
  for (const part of parts) {
    if (part.kind === 'text') {
      line.appendChild(document.createTextNode(part.text));
    } else {
      line.appendChild(
        el('a', {
          class: 'text-gold underline',
          href: `mailto:${email}`,
          text: String(part.value),
        }),
      );
    }
  }
  return line;
}

/**
 * Legal links: the index, plus every PUBLISHED document version.
 *
 * The index link is unconditional so there is always a route to the legal
 * section, even before an operator publishes anything.
 */
function legalNav(legal: readonly LegalDocumentLink[]): HTMLElement {
  const items: Child[] = [
    linkItem(ROUTES.legal, t('legal.index_title')),
    ...legal.map((document) => linkItem(legalDocumentPath(document.slug), document.title)),
    /*
     * THE WAY BACK TO THE COOKIE NOTICE.
     *
     * A real anchor, not a button: it survives this footer being re-rendered by
     * the i18n and currency subscriptions (no listener to re-bind), and if the
     * notice module is ever absent the worst case is an inert hash rather than
     * a dead control. `cookie-consent.ts` picks it up through one delegated
     * document listener — see the header there.
     *
     * Withdrawing must be as easy as consenting (GDPR Art. 7(3)), so it sits in
     * the footer of every page, beside the policy it governs.
     */
    el('li', {}, [
      el('a', {
        class: LINK_CLASS,
        href: COOKIE_SETTINGS_HASH,
        'data-cookie-settings': 'true',
        'data-testid': 'footer-cookie-settings',
        text: t('cookies.banner.manage'),
      }),
    ]),
  ];
  return navColumn('footer-legal', t('footer.legal_label'), items);
}

function supportNav(info: GameSiteInfo): HTMLElement {
  const items: Child[] = [linkItem(ROUTES.faq, t('nav.faq')), linkItem(ROUTES.news, t('nav.news'))];
  if (info.company.email !== '') {
    items.push(
      el('li', {}, [
        el('a', {
          class: LINK_CLASS,
          href: `mailto:${info.company.email}`,
          text: t('faq.contact'),
        }),
      ]),
    );
  }
  return navColumn('footer-support', t('footer.support_label'), items);
}

/**
 * Social profiles. Omitted entirely when there are none — which is every
 * preview build, and every deployment before someone fills the settings in.
 */
function communityNav(info: GameSiteInfo): HTMLElement | null {
  if (info.socialLinks.length === 0) return null;
  const items = info.socialLinks.map((link) =>
    el('li', {}, [
      el('a', {
        class: `${SOCIAL_LINK_CLASS} flex items-center gap-2`,
        href: link.url,
        target: '_blank',
        // noopener is a security control, not a nicety: without it the opened
        // page gets a handle on ours through `window.opener`.
        rel: 'noopener noreferrer',
      }, [
        // The mark is DECORATIVE: the platform name sits right beside it and is
        // the accessible name, so a link whose icon is blank or whose file is
        // missing still reads correctly — it just loses a picture.
        // …and it is a MASK, not an <img>. The brand SVGs are authored with
        // fill="currentColor", but an <img> renders its source as a separate
        // document: `currentColor` there resolves against the image's own
        // initial colour (black), not this link's, which put a black glyph on
        // the dark footer. Masking a `bg-current` box paints the shape in the
        // link's colour without touching the brand geometry.
        link.icon === '' ? null : socialMark(link.icon),
        platformLabel(link.platform),
        el('span', { class: VISUALLY_HIDDEN, text: ` ${t('a11y.external_link')}` }),
      ]),
    ]),
  );
  return navColumn('footer-community', t('footer.community_label'), items);
}

/**
 * The brand mark as a tinted silhouette. Decorative: the platform name sits
 * beside it and supplies the accessible name, so a mask that fails to load
 * costs a picture and nothing else.
 */
function socialMark(icon: string): HTMLElement {
  // Quotes and backslashes cannot survive into a style attribute unescaped —
  // the stem is operator-supplied and this is a CSS context, not a DOM one.
  const url = `url("${socialIconUrl(icon).replace(/["\\]/g, '')}") center/contain no-repeat`;
  return el('span', {
    class: 'h-4 w-4 shrink-0 bg-current',
    'aria-hidden': 'true',
    style: `-webkit-mask:${url};mask:${url}`,
  });
}

function navColumn(id: string, heading: string, items: readonly Child[]): HTMLElement {
  return el('nav', { 'aria-labelledby': id }, [
    el('h2', { id, class: COLUMN_HEADING_CLASS, text: heading }),
    el('ul', { class: 'flex flex-col' }, items),
  ]);
}

function linkItem(href: string, label: string): HTMLElement {
  return el('li', {}, [el('a', { class: LINK_CLASS, href, text: label })]);
}

/**
 * Platform keys arrive as settings names (`discord`, `youtube`). They are
 * proper nouns, so they are NOT translated — only cased for display.
 */
function platformLabel(platform: string): HTMLElement {
  const label = platform.charAt(0).toUpperCase() + platform.slice(1);
  return el('span', { text: label });
}

/**
 * The enabled payment brands.
 *
 * DISPLAY ONLY. Core's payment-methods admin is a marketing surface — it does
 * not route money and no provider adapter is registered anywhere. Showing a
 * Visa mark here is a claim about what a player will be offered at checkout, so
 * it renders exactly what an operator enabled and nothing more.
 *
 * Each mark carries the brand name as alt text rather than `alt=""`: "we take
 * Visa and PayPal" is information, not decoration.
 */
function paymentStrip(info: GameSiteInfo): HTMLElement | null {
  if (info.paymentMethods.length === 0) return null;
  return el('section', { 'aria-labelledby': 'footer-payments' }, [
    el('h2', {
      id: 'footer-payments',
      class: COLUMN_HEADING_CLASS,
      text: t('footer.payment_methods_label'),
    }),
    el(
      'ul',
      { class: 'flex flex-wrap items-center gap-2' },
      info.paymentMethods.map((method) => paymentMark(method)),
    ),
  ]);
}

/**
 * One brand mark, and its name when the mark will not load.
 *
 * The admin form takes the icon as a FREE-TEXT stem and the src is built from
 * it unconditionally, so a typo or a brand whose SVG was never shipped used to
 * leave a broken-image glyph in the footer of every page. The social column
 * already solves this — it treats its mark as decorative and keeps the text
 * label — and there is no reason the two surfaces should disagree, so a mark
 * that fails to load is replaced by the brand's name. Either way the visitor
 * learns that the site takes PayPal.
 */
function paymentMark(method: PaymentMethodRef): HTMLElement {
  const item = el('li', { class: 'flex items-center' });
  const img = el('img', {
    class: 'h-6 w-auto',
    src: paymentIconUrl(method),
    alt: method.name,
    width: 38,
    height: 24,
    loading: 'lazy',
    decoding: 'async',
  });
  img.addEventListener(
    'error',
    () => {
      clear(item);
      item.appendChild(
        el('span', {
          class: 'rounded border border-border px-2 py-1 text-text-dim',
          'data-testid': 'footer-payment-fallback',
          text: method.name,
        }),
      );
    },
    { once: true },
  );
  item.appendChild(img);
  return item;
}

/**
 * `© 2026 Acme Ltd. All rights reserved.`
 *
 * The year is formatted with `formatNumber(..., {useGrouping:false})` rather
 * than string concatenation because several locales render digits in their own
 * numerals, and "© ٢٠٢٦" is correct there while "© 2026" is not.
 */
function copyrightLine(info: GameSiteInfo): string {
  return t('footer.copyright', {
    year: formatNumber(new Date().getFullYear(), { useGrouping: false }),
    company: brandName(info),
  });
}

/**
 * Company registration — WHATEVER HALF THE OPERATOR HAS FILLED IN.
 *
 * This used to require both, and the two are filled in at different times: a
 * fresh install ships a registration number placeholder with no jurisdiction,
 * so an operator who typed a real number saw the line stay invisible with
 * nothing anywhere to say why. Each half is a complete statement on its own —
 * "Registered in Estonia." and "Company registration number 16123456." are both
 * true and both useful — so each half gets its own sentence rather than being
 * concatenated into one, which is the only way the word order survives
 * translation.
 */
function registrationLine(info: GameSiteInfo): HTMLElement | null {
  const jurisdiction = info.company.jurisdiction.trim();
  const number = info.company.registration.trim();
  const text =
    jurisdiction !== '' && number !== ''
      ? t('footer.registration', { jurisdiction, number })
      : jurisdiction !== ''
        ? t('footer.registration_jurisdiction', { jurisdiction })
        : number !== ''
          ? t('footer.registration_number', { number })
          : null;
  if (text === null) return null;
  return el('p', { 'data-testid': 'footer-registration', text });
}

/**
 * A BUTTON, not `<a href="#top">`.
 *
 * `main.ts` intercepts same-origin anchor clicks to drive the History router;
 * a hash link there is a routing edge case waiting to be re-broken. Scrolling
 * is a behaviour, not a destination, so a button is also the correct element.
 * Focus is moved to the page's `<main>` so a keyboard user's caret follows the
 * scroll instead of staying at the bottom of the document.
 */
function backToTop(): HTMLElement {
  const button = el('button', {
    type: 'button',
    class: `${TAP_TARGET} px-3 rounded border border-border text-text-dim hover:text-text`,
    text: t('footer.back_to_top'),
  });
  skinButton(button, { variant: 'ghost' });
  attachPress(button, { onPress: () => {
    try {
      globalThis.scrollTo?.({ top: 0, behavior: 'smooth' });
    } catch {
      /* jsdom and some embedded webviews have no scroll implementation */
    }
    const main = document.getElementById('main');
    if (main === null) return;
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
  } });
  return button;
}

/**
 * Preview builds say so.
 *
 * The blanked company and missing payment methods are a deliberate server-side
 * decision, and a visitor (usually a reviewer or a stakeholder) is better
 * served by being told than by wondering whether the build is broken.
 */
function previewNotice(info: GameSiteInfo): HTMLElement | null {
  if (!info.preview) return null;
  return el('p', {
    class: 'rounded border border-border bg-surface px-3 py-2 text-text-dim',
    role: 'note',
    'data-testid': 'footer-preview-notice',
    text: t('footer.preview_notice'),
  });
}
